import clsx from 'clsx';
import { cloneDeep } from 'lodash';
import { useEffect, useState } from 'react';
import { Question } from 'src/types';
import { ImageObjectComponentDel } from '../ImageObjectComponentDel';
import { Check } from '../common/Check';
import { Typography } from '../common/Typography';
import { DocumentCard } from '../ib/DocumentCard';
import { ImageCard } from '../ib/ImageCard';

interface SuperSurveyViewComponentProps {
  surveyContent?: string;
  setContent: (content: any) => void;
  content: any;
  readOnly?: boolean;
}

export function SuperSurveyViewComponent({
  surveyContent,
  setContent,
  content = {},
  readOnly,
}: SuperSurveyViewComponentProps) {
  const [survey, setSurvey] = useState<Question[]>([]);
  useEffect(() => {
    if (surveyContent) {
      try {
        setSurvey(JSON.parse(surveyContent));
      } catch (err) {
        console.error(err);
      }
    }
  }, [surveyContent]);

  if (survey.length === 0) {
    return null;
  }

  return (
    <section className="flex w-full flex-col gap-6">
      {survey?.map((question) => {
        const onChangeValue = (value: string) => {
          const _content = cloneDeep(content);
          _content[question.id] = value;
          setContent(_content);
        };
        const onChangeCheckboxValue = (value: string) => {
          const _content = cloneDeep(content);
          if (_content[question.id]) {
            if (_content[question.id].includes(value)) {
              _content[question.id] = _content[question.id].filter((v: string) => v !== value);
            } else {
              _content[question.id].push(value);
            }
          } else {
            _content[question.id] = [value];
          }
          setContent(_content);
        };
        const value = content[question.id] || '';

        switch (question.type) {
          case 'image':
            return (
              <div className="flex w-full flex-col space-y-2" key={question.id}>
                <Typography variant="title3" className="font-semibold">
                  {question.title}
                </Typography>
                {value && <ImageObjectComponentDel id={question.id} imageObjet={{ image: value, isDelete: false }} />}
              </div>
            );
          case 'file':
            return (
              <div className="flex w-full flex-col space-y-2" key={question.id}>
                <Typography variant="title3" className="font-semibold">
                  {question.title}
                </Typography>
                {value &&
                  (value.includes('image') ? (
                    <ImageCard id={question.id} imageObjet={{ image: value, isDelete: false }} />
                  ) : (
                    <DocumentCard id={question.id} isDownload documentObjet={{ document: value, isDelete: false }} />
                  ))}
              </div>
            );
          case 'text':
            return (
              <div className="flex w-full flex-col gap-1" key={question.id}>
                <Typography variant="title3" className="font-semibold">
                  {question.title}
                </Typography>
                <Typography variant="body1">{value}</Typography>
              </div>
            );
          case 'longtext':
            return (
              <div className="flex w-full flex-col gap-1" key={question.id}>
                <Typography variant="title3" className="font-semibold">
                  {question.title}
                </Typography>
                <Typography variant="body1">{value}</Typography>
              </div>
            );
          case 'checkbox':
            return (
              <div className="flex flex-col gap-2" key={question.id}>
                <Typography variant="title3" className="font-semibold">
                  {question.title}
                </Typography>
                <div className="flex flex-col gap-2">
                  {question?.choices?.map((c: any, index: number) => (
                    <div
                      key={index}
                      className={`flex w-full flex-row items-center gap-4 rounded-xl border border-primary-gray-200 p-4 shadow-[0_4px_8px_0_#F4F6F8] ${readOnly && 'bg-primary-gray-50'} overflow-hidden`}
                    >
                      <div className="flex flex-1 flex-col">
                        <Typography variant="title3">{c}</Typography>
                      </div>
                      <Check.Basic
                        name={String(question.id)}
                        checked={value?.includes(c) || false}
                        disabled={readOnly}
                        onChange={() => {
                          onChangeCheckboxValue(c);
                        }}
                      />
                    </div>
                  ))}
                </div>
              </div>
            );
          case 'radiobutton':
            return (
              <div className="flex flex-col gap-2" key={question.id}>
                <Typography variant="title3" className="font-semibold">
                  {question.title}
                </Typography>
                <div className="flex flex-col gap-2">
                  {question?.choices?.map((c: any, index: number) => (
                    <label
                      key={index}
                      onClick={() => onChangeValue(c)}
                      className={`flex w-full flex-row items-center gap-4 rounded-xl border border-primary-gray-200 p-4 shadow-[0_4px_8px_0_#F4F6F8] ${readOnly && value === c && 'bg-primary-gray-50'} overflow-hidden`}
                    >
                      <div className="flex flex-1 flex-col">
                        <Typography variant="title3">{c}</Typography>
                      </div>
                      <span
                        className={clsx('flex items-center justify-center rounded-full border-2 transition-colors', {
                          'border-primary-gray-200 bg-white': !readOnly,
                          'border-primary-gray-200 bg-primary-gray-50': readOnly && value === c,
                          'cursor-not-allowed': readOnly,
                        })}
                        style={{ width: 24, height: 24 }}
                      >
                        {value === c && (
                          <span
                            className={clsx('rounded-full', {
                              'bg-primary-orange-800': !readOnly,
                              'bg-primary-gray-300': readOnly,
                            })}
                            style={{
                              width: `${24 * 0.5}px`,
                              height: `${24 * 0.5}px`,
                            }}
                          />
                        )}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            );
        }
      })}
    </section>
  );
}
